(() => {
  'use strict';
  const root = document.querySelector('[data-yg-customer-portal]');
  if (!root) return;
  const config = JSON.parse(root.querySelector('[data-portal-config]').textContent);
  const t = config.messages;
  const key = 'yugen-customer-session-v1', flowKey = 'yugen-customer-flow-v1';
  const find = selector => root.querySelector(selector);
  const status = find('[data-portal-status]');
  const signedOut = find('[data-portal-signed-out]'), signedIn = find('[data-portal-signed-in]');
  let auth, api, session, customer, pageInfo, busy = false;
  const query = `query YugenAccount($after: String) { customer { id displayName firstName lastName emailAddress { emailAddress } orders(first: 20, after: $after, reverse: true) { nodes { id name processedAt financialStatus fulfillmentStatus totalPrice { amount currencyCode } lineItems(first: 50) { nodes { id name title quantity totalPrice { amount currencyCode } } pageInfo { hasNextPage endCursor } } } pageInfo { hasNextPage endCursor } } } }`;
  const itemsQuery = `query YugenOrderItems($id: ID!, $after: String) { order(id: $id) { lineItems(first: 50, after: $after) { nodes { id name title quantity totalPrice { amount currencyCode } } pageInfo { hasNextPage endCursor } } } }`;
  const json = async (url, options = {}) => {
    let response;
    try { response = await fetch(url, { ...options, cache: 'no-store', credentials: 'omit' }); }
    catch { throw new Error('network-' + new URL(url, location.origin).pathname); }
    if (!response.ok) throw new Error('request-' + response.status + '-' + new URL(url, location.origin).pathname);
    return response.json();
  };
  const endpoint = value => {
    const url = new URL(value);
    if (url.protocol !== 'https:' || url.hostname !== 'account.yugen.no') throw new Error('endpoint');
    return url.href;
  };
  async function discover() {
    [auth, api] = await Promise.all([json('/.well-known/openid-configuration'), json('/.well-known/customer-account-api')]);
    ['authorization_endpoint', 'token_endpoint', 'end_session_endpoint', 'jwks_uri'].forEach(name => endpoint(auth[name]));
    endpoint(api.graphql_api);
    if (auth.issuer !== 'https://shopify.com/authentication/84665270527') throw new Error('issuer');
  }
  const base64url = bytes => btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  const decode = value => Uint8Array.from(atob(value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - value.length % 4) % 4)), char => char.charCodeAt(0));
  const random = () => base64url(crypto.getRandomValues(new Uint8Array(32)));
  function safeReturn(value) {
    const url = new URL(value, location.origin);
    return url.origin === location.origin && /^\/(?:[a-z]{2}(?:-[a-zA-Z]{2})?\/)?pages\/(min-konto|ordreoversikt)\/?$/.test(url.pathname) ? url.pathname : config.accountPath;
  }
  const message = value => { status.textContent = value; };
  const clearSession = () => { sessionStorage.removeItem(key); session = null; customer = null; signedIn.hidden = true; signedOut.hidden = false; find('[data-portal-orders]').replaceChildren(); };
  async function login() {
    if (busy) return;
    busy = true;
    message(t.loading);
    try {
      await discover();
      const verifier = random(), state = random(), nonce = random();
      const redirect = location.origin + '/pages/min-konto';
      sessionStorage.setItem(flowKey, JSON.stringify({ verifier, state, nonce, redirect, returnTo: safeReturn(location.pathname), createdAt: Date.now() }));
      const challenge = base64url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));
      const url = new URL(auth.authorization_endpoint);
      Object.entries({ client_id: config.clientId, response_type: 'code', scope: 'openid email customer-account-api:full', redirect_uri: redirect, state, nonce, code_challenge: challenge, code_challenge_method: 'S256', locale: document.documentElement.lang || 'nb' }).forEach(([name, value]) => url.searchParams.set(name, value));
      const hint = sessionStorage.getItem('yugen-login-hint-v1');
      sessionStorage.removeItem('yugen-login-hint-v1');
      if (hint && hint.length < 255) url.searchParams.set('login_hint', hint);
      location.assign(url.href);
    } catch { message(t.error); busy = false; }
  }
  async function verifyIdentity(token, nonce) {
    const parts = token.split('.');
    if (parts.length !== 3) throw new Error('identity');
    const header = JSON.parse(new TextDecoder().decode(decode(parts[0])));
    const claims = JSON.parse(new TextDecoder().decode(decode(parts[1])));
    if (header.alg !== 'RS256') throw new Error('identity-algorithm');
    if (claims.iss !== auth.issuer) throw new Error('identity-issuer');
    if (!(Array.isArray(claims.aud) ? claims.aud : [claims.aud]).includes(config.clientId)) throw new Error('identity-audience');
    if (claims.exp * 1000 < Date.now()) throw new Error('identity-expiry');
    if (claims.nonce !== nonce) throw new Error('identity-nonce');
    if (!Number.isFinite(claims.exp) || !Number.isFinite(claims.iat) || claims.iat * 1000 > Date.now() + 60000 || typeof claims.sub !== 'string' || !claims.sub) throw new Error('identity-claims');
    if (claims.azp && claims.azp !== config.clientId) throw new Error('identity-party');
    if (Array.isArray(claims.aud) && claims.aud.length > 1 && claims.azp !== config.clientId) throw new Error('identity-party');
  }
  async function tokenRequest(body) {
    return json(auth.token_endpoint, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: new URLSearchParams({ client_id: config.clientId, ...body }) });
  }
  function saveSession(tokens) {
    if (!tokens.access_token || !Number.isFinite(tokens.expires_in)) throw new Error('token');
    session = { accessToken: tokens.access_token, refreshToken: tokens.refresh_token || session?.refreshToken, idToken: tokens.id_token || session?.idToken, expiresAt: Date.now() + tokens.expires_in * 1000 };
    sessionStorage.setItem(key, JSON.stringify(session));
  }
  async function callback() {
    const params = new URLSearchParams(location.search);
    if (!params.has('code') && !params.has('error')) return false;
    const flow = JSON.parse(sessionStorage.getItem(flowKey) || 'null');
    sessionStorage.removeItem(flowKey);
    history.replaceState(null, '', location.pathname);
    if (!flow || flow.state !== params.get('state') || Date.now() - flow.createdAt > 600000 || (params.has('iss') && params.get('iss') !== auth.issuer)) throw new Error('state');
    if (params.has('error')) throw new Error('login');
    const tokens = await tokenRequest({ grant_type: 'authorization_code', code: params.get('code'), redirect_uri: flow.redirect, code_verifier: flow.verifier });
    await verifyIdentity(tokens.id_token, flow.nonce);
    saveSession(tokens);
    const returnTo = safeReturn(flow.returnTo);
    if (returnTo !== location.pathname) { location.replace(returnTo); return true; }
    return false;
  }
  async function access() {
    if (!session) throw new Error('signed-out');
    if (session.expiresAt < Date.now() + 30000) {
      if (!session.refreshToken) { clearSession(); throw new Error('signed-out'); }
      try { saveSession(await tokenRequest({ grant_type: 'refresh_token', refresh_token: session.refreshToken })); }
      catch (error) { clearSession(); throw error; }
    }
    return session.accessToken;
  }
  async function graph(operation, variables) {
    const response = await fetch(api.graphql_api, { method: 'POST', cache: 'no-store', credentials: 'omit', headers: { 'Content-Type': 'application/json', Authorization: await access() }, body: JSON.stringify({ query: operation, variables }) });
    if (response.status === 401 || response.status === 403) { clearSession(); throw new Error('signed-out'); }
    const data = await response.json();
    if (!response.ok || data.errors?.length) throw new Error('data');
    return data.data;
  }
  const element = (tag, text, className) => { const node = document.createElement(tag); if (text != null) node.textContent = text; if (className) node.className = className; return node; };
  const money = value => new Intl.NumberFormat(document.documentElement.lang || 'nb', { style: 'currency', currency: value.currencyCode }).format(Number(value.amount));
  function itemRows(items, body) {
    for (const item of items) { const row = element('tr'); [item.name || item.title, item.quantity, money(item.totalPrice)].forEach(text => row.append(element('td', text))); body.append(row); }
  }
  function renderOrder(order) {
    const details = element('details', null, 'yg-portal-order');
    const summary = element('summary');
    const date = new Intl.DateTimeFormat(document.documentElement.lang || 'nb').format(new Date(order.processedAt));
    const labels = [t[order.financialStatus] || order.financialStatus, t[order.fulfillmentStatus] || order.fulfillmentStatus].filter(Boolean).join(' · ');
    [element('strong', order.name), element('span', date), element('span', labels), element('strong', money(order.totalPrice))].forEach(node => summary.append(node));
    details.append(summary);
    const tableWrap = element('div', null, 'yg-table-scroll'), table = element('table'), head = element('thead'), row = element('tr'), body = element('tbody');
    [t.product, t.quantity, t.total].forEach(text => row.append(element('th', text)));
    head.append(row); table.append(head, body); tableWrap.append(table); itemRows(order.lineItems.nodes, body); details.append(tableWrap);
    let itemsPage = order.lineItems.pageInfo;
    if (itemsPage.hasNextPage) {
      const moreItems = element('button', t.more, 'yg-button yg-button-secondary'); moreItems.type = 'button';
      moreItems.addEventListener('click', async () => {
        moreItems.disabled = true;
        try { const data = await graph(itemsQuery, { id: order.id, after: itemsPage.endCursor }); itemRows(data.order.lineItems.nodes, body); itemsPage = data.order.lineItems.pageInfo; moreItems.hidden = !itemsPage.hasNextPage; }
        catch { message(t.error); } finally { moreItems.disabled = false; }
      }); details.append(moreItems);
    }
    return details;
  }
  async function loadOrders(append = false) {
    if (busy) return;
    busy = true; message(t.loading); find('[data-portal-more]').disabled = true;
    try {
      const data = await graph(query, { after: append ? pageInfo?.endCursor : null });
      if (!data.customer) { clearSession(); throw new Error('signed-out'); }
      customer = data.customer;
      find('[data-portal-name]').textContent = customer.displayName;
      find('[data-portal-email]').textContent = customer.emailAddress?.emailAddress || '—';
      const list = find('[data-portal-orders]');
      if (!append) list.replaceChildren();
      customer.orders.nodes.forEach(order => list.append(renderOrder(order)));
      find('[data-portal-empty]').hidden = list.childElementCount > 0;
      pageInfo = customer.orders.pageInfo; find('[data-portal-more]').hidden = !pageInfo.hasNextPage;
      signedOut.hidden = true; signedIn.hidden = false; message('');
    } catch (error) { root.dataset.portalError = error.message; message(t.error); find('[data-portal-retry]').hidden = false; }
    finally { busy = false; find('[data-portal-more]').disabled = false; }
  }
  async function logout() {
    const idToken = session?.idToken; clearSession(); message('');
    if (!idToken) return;
    try { await discover(); const url = new URL(auth.end_session_endpoint); url.searchParams.set('id_token_hint', idToken); url.searchParams.set('post_logout_redirect_uri', location.origin + '/pages/min-konto'); location.assign(url.href); }
    catch { message(t.error); }
  }
  find('[data-portal-login]').addEventListener('click', login);
  find('[data-portal-logout]').addEventListener('click', logout);
  find('[data-portal-more]').addEventListener('click', () => loadOrders(true));
  find('[data-portal-retry]').addEventListener('click', async () => { find('[data-portal-retry]').hidden = true; try { await discover(); if (session) await loadOrders(); else message(''); } catch { message(t.error); find('[data-portal-retry]').hidden = false; } });
  async function initialize() {
    signedIn.hidden = true; signedOut.hidden = true; find('[data-portal-orders]').replaceChildren(); message(t.loading);
    try { const autoLogin = new URLSearchParams(location.search).get('login') === '1'; session = JSON.parse(sessionStorage.getItem(key) || 'null'); await discover(); if (await callback()) return; if (autoLogin && !session) { history.replaceState(null, '', location.pathname); await login(); return; } if (session) await loadOrders(); else { signedOut.hidden = false; message(''); } }
    catch (error) { root.dataset.portalError = error.message; signedOut.hidden = false; message(t.error); find('[data-portal-retry]').hidden = false; }
  }
  window.addEventListener('pageshow', event => { if (event.persisted) initialize(); });
  initialize();
})();


